<template>
  <div class="radar" :class="{ 'rader--scalable': scalable }">
    <svg class="radar__chart" width="100%" height="100%" viewBox="0 0 300 300">
      <RadarMesh
        class="radar__mesh"
        :radius="config.circleMaxRadius"
        :circle-count="config.circleCount"
        :circle-labels="config.circleLabels"
      />
    </svg>

    <div ref="chart" class="radar__chartOld" />
    <!--PPP Sections-->
    <div v-if="showSections" class="radar__sections">
      <RadarSection
        v-for="(pppSection, index) in pppSections"
        :key="pppSection.id"
        :is-active="pppSection.isActive"
        :active-kpi-index="pppSection.activeKpiIndex"
        :section-color="pppSection.color"
        :class="pppSection.class"
        :rotation="240 - index * 120"
        :label-name="pppSection.label"
        :section-labels="pppSection.labels"
        :show-k-p-i-labels="showKPILabels"
        :section-index="index"
        @click-dot="$emit('click-kpi', $event)"
      />
    </div>

    <!-- svg gradient for chart bg -->
    <svg class="radar__gradient">
      <defs>
        <linearGradient id="chartBgGradient">
          <stop offset="0%" :stop-color="config.bgGradientStartColor" />
          <stop offset="100%" :stop-color="config.bgGradientEndColor" />
        </linearGradient>
      </defs>
    </svg>
  </div>
</template>

<script>
import { TweenMax } from 'gsap/all';
import RadarChartInstance from './lib/RadarChart';
import RadarMesh from './RadarMesh';
import RadarSection from './RadarSection';
import store from '../../store';
import { pppSections } from '../../store/trivia';

const DEFAULT_CONFIG = {
  w: 400,
  h: 400,
  maxValue: 1.3,
  circleCount: 4,
  circleMaxRadius: 110,
  bgGradientStartColor: '#7152e4',
  bgGradientEndColor: '#10dad8',
};

export default {
  name: 'RadarChart',

  components: {
    RadarMesh,
    RadarSection,
  },

  props: {
    kpiData: {
      type: Array,
      default: () => [],
    },

    pppActiveSections: {
      type: Array,
      default: () => [],
    },

    kpiActiveIndex: {
      type: Number,
      default: -1,
    },

    activeKPISectionIndex: {
      type: Number,
      default: -1,
    },

    showSections: {
      type: Boolean,
      default: true,
    },

    showKPILabels: {
      type: Boolean,
      default: false,
    },

    // Use only when showSections and showKPILabels are set to false.
    // Otherwise it will break the design (alignment of sections and labels).
    scalable: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      store,
    };
  },

  computed: {
    // TODO: put computed in store as export
    config() {
      return {
        ...DEFAULT_CONFIG,

        colors: [
          { color: '#ffffff', isActive: true },
          {
            color: pppSections.PEOPLE.color,
            isActive: this.isActivePPPSection(pppSections.PEOPLE.id),
          },
          {
            color: pppSections.PROFIT.color,
            isActive: this.isActivePPPSection(pppSections.PROFIT.id),
          },
          {
            color: pppSections.PLANET.color,
            isActive: this.isActivePPPSection(pppSections.PLANET.id),
          },
        ],
      };
    },

    pppSections() {
      return [
        {
          id: 'people',
          label: this.$t(pppSections.PEOPLE.labelId),
          isActive: this.isActivePPPSection(pppSections.PEOPLE.id),
          color: pppSections.PEOPLE.color,
          class: 'radar__people',
          activeKpiIndex: this.kpiActiveIndex - 6,
          labels: [
            { label: this.$t('kpi.fields.employeeHappiness'), left: 180, top: -51 },
            { label: this.$t('kpi.fields.healthService'), left: 259, top: 24 },
            { label: this.$t('kpi.education'), left: 281, top: 116 },
          ],
        },
        {
          id: 'profit',
          label: this.$t(pppSections.PROFIT.labelId),
          isActive: this.isActivePPPSection(pppSections.PROFIT.id),
          color: pppSections.PROFIT.color,
          class: 'radar__profit',
          activeKpiIndex: this.kpiActiveIndex - 3,
          labels: [
            { label: this.$t('kpi.fields.netProfit'), left: 159, top: -56 },
            { label: this.$t('kpi.fields.innovationRate'), left: 266, top: 39 },
            { label: this.$t('kpi.fields.customerSatisfaction'), left: 289, top: 192 },
          ],
        },
        {
          id: 'planet',
          label: this.$t(pppSections.PLANET.labelId),
          isActive: this.isActivePPPSection(pppSections.PLANET.id),
          color: pppSections.PLANET.color,
          class: 'radar__planet',
          activeKpiIndex: this.kpiActiveIndex,
          labels: [
            { label: this.$t('kpi.co2e'), left: 190, top: -10 },
            { label: this.$t('kpi.energy'), left: 274, top: 63 },
            { label: this.$t('kpi.plastic'), left: 292, top: 168 },
          ],
        },
      ];
    },
  },

  watch: {
    activeKPISectionIndex(activeSectionIndex) {
      if (activeSectionIndex != -1) {
        if (activeSectionIndex == 0) {
          TweenMax.to('.radar-chart-serie1', 0.5, { autoAlpha: 0 });
          TweenMax.to('.radar-chart-serie2', 0.5, { autoAlpha: 0 });
          TweenMax.to('.radar-chart-serie3', 0.5, { autoAlpha: 1 });
        }
        if (activeSectionIndex == 1) {
          TweenMax.to('.radar-chart-serie1', 0.5, { autoAlpha: 0 });
          TweenMax.to('.radar-chart-serie2', 0.5, { autoAlpha: 1 });
          TweenMax.to('.radar-chart-serie3', 0.5, { autoAlpha: 0 });
        }
        if (activeSectionIndex == 2) {
          TweenMax.to('.radar-chart-serie1', 0.5, { autoAlpha: 1 });
          TweenMax.to('.radar-chart-serie2', 0.5, { autoAlpha: 0 });
          TweenMax.to('.radar-chart-serie3', 0.5, { autoAlpha: 0 });
        }
      }
    },
  },

  mounted() {
    this.drawChart();
  },

  methods: {
    drawChart() {
      RadarChartInstance.draw(this.$refs.chart, this.kpiData, this.config);
    },

    isActivePPPSection(pppSectionId) {
      if (this.pppActiveSections) {
        return this.pppActiveSections.map(({ id }) => id).includes(pppSectionId);
      }
      return -1;
    },
  },
};
</script>

<style lang="scss" scoped>
$radar-size: 300px;

.radar {
  position: relative;
  text-align: center;
}

.radar__gradient {
  position: absolute;
}

.radar__planet {
  transform: rotate(0deg);
}

.radar__people {
  transform: rotate(240deg);
}

.radar__profit {
  transform: rotate(120deg);
}

.radar:not(.rader--scalable) {
  padding-bottom: 40px;

  // media querries for scaling
  @media only screen and (min-width: $bp-450) {
    transform: scale(1);
  }

  @media only screen and (max-width: $bp-450) {
    transform: scale(0.8);
    margin-top: -20px !important;
    margin-bottom: -20px !important;
  }

  @media only screen and (max-width: $bp-400) {
    transform: scale(0.7);
    margin-top: -40px !important;
    margin-bottom: -40px !important;
  }

  @media only screen and (min-width: $bp-1024) {
    transform: scale(1.3);
    margin-top: 170px;
    margin-bottom: 90px;
  }

  @media only screen and (min-width: $bp-1590) {
    transform: scale(1.67);
    margin-top: 170px;
  }
}

.radar__chart,
.radar__chartOld,
.radar__mesh,
.radar__sections {
  width: $radar-size;
  height: $radar-size;
  padding-left: auto;
  padding-right: auto;

  .rader--scalable & {
    width: auto;
    height: auto;
  }
}

.radar__sections,
.radar__chartOld {
  position: absolute;
  top: 0;
  left: 50%;
  transform: translateX(-50%);

  .rader--scalable & {
    left: 0;
    width: 100%;
    height: 100%;
    transform: none;
  }
}
</style>
